import P from '../pages';
import { setProfileElementsFromState, generateProfileElementObject } from '../../../shared/profileElementHelpers';
import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import ReactHtmlParser from 'react-html-parser';
import FormHeader from './FormHeader';
import Alert from './Alert';
import ReactMarkdown from 'react-markdown';
import { CustomFormGroup, Form } from '../../../shared/components';
import FormControl from 'react-bootstrap/lib/FormControl';
import ControlLabel from 'react-bootstrap/lib/ControlLabel';
import CheckboxOrRadioGroup from './CheckboxOrRadioGroup';
import { handleSelection, changeState, yesNoOptions } from '../../../shared/utilities';
import { ameOrUsa } from '../helpers'
import { pageValid, fieldsToValidate } from '../../../shared/validator';
import withDefaults from './withDefaults'
import { productSubmit } from '../actions';
import { defaultMessages } from '../../../libs/i18n/default';
import { get, lowerCase, snakeCase } from 'lodash'

const Product = ({ dispatch, page, content, user, territory, environment, intl: { formatMessage} }) => {
  const thisPage = P.PRODUCT_PAGE;
  const [submitted, setSubmitted] = useState(false)
  const productSelections = [...new Set(user.guest.product_options.map(t => t.kind).filter(t => t != 'other'))]
  const dietSelections = useSelector(state => state.localProfileElements.elements.preferences.dietary_requirements_selections)
  const insuranceSelections = useSelector(state => state.localProfileElements.elements.service.insurance_selections)
  const academicSupportSelections = useSelector(state => state.localProfileElements.elements.service.academic_support_selections)
  const guardianshipSelections = useSelector(state => state.localProfileElements.elements.service.guardianship_selections)
  const productLocked = user.guest.product_locked
  const productOptions = ['complete','traditional','dinneronly','breakfastonly','nomeals'].filter(i => productSelections.includes(i)).map(t => {
    return ([<ReactMarkdown className="product-explanation" source={content.pages.service_level_options[snakeCase(user.guest.type.code)][t]} />, t])
  })
  const insuranceOptions = insuranceSelections.map(t => {
    let options = territory.code == 'SRSC' ? content.pages.insurance_options[snakeCase(user.guest.type.code)] : content.pages.insurance_options
    return(
      options &&
      [<ReactMarkdown className="product-explanation" source={options[lowerCase(t[0])]} />, t[1]]
    )
  })
  const academicSupportOptions = academicSupportSelections.map(t => (
    content.pages.academic_support_options &&
    [<ReactMarkdown className="product-explanation" source={content.pages.academic_support_options[lowerCase(t[0])]} />, t[1]]
  ))
  const guardianshipOptions = guardianshipSelections.map(t => (
    content.pages.guardianship_options &&
    [<ReactMarkdown className="product-explanation" source={content.pages.guardianship_options[lowerCase(t[0])]} />, t[1]]
  ))
  const premiumStudentCareOptions = ['true', 'false'].map(t => (
    content.pages.premium_student_care_options &&
    [<ReactMarkdown className="product-explanation" source={content.pages.premium_student_care_options[t]} />, t]
  ))
  const office = get(user.guest, 'office')

  const [fields, setFields] = useState({
    serviceLevel: {
      val: setProfileElementsFromState('', 'service', 'service_level'),
      validationKey: !productLocked && productOptions.length > 0 ? 'profile_elements_service_service_level' : '',
      profileElement: 'service_level'
    },
    dietaryRequirements: {
      ...generateProfileElementObject('', 'preferences', 'dietary_requirements'),
      profileElement: !ameOrUsa(territory) && 'dietary_requirements'
    },
    dietaryRequirementsOther: {
      ...generateProfileElementObject('', 'preferences', 'dietary_requirements_other'),
      profileElement: !ameOrUsa(territory) && 'dietary_requirements',
      dependentObj: 'dietaryRequirements',
      dependencyMet: (dependentObj = fieldsRef.current.dietaryRequirements) => {return(dependentObj.val.includes('Other'))}
    },
    insurance: generateProfileElementObject('', 'service', 'insurance'),
    guardianship: generateProfileElementObject('', 'service', 'guardianship'),
    academicSupport: generateProfileElementObject('', 'service', 'academic_support'),
    premiumStudentCareRequired: generateProfileElementObject('', 'service', 'premium_student_care_required'),
    airportPickupRequired: {
      ...generateProfileElementObject('', 'placement', 'airport_pickup_required'),
      profileElement: ameOrUsa(territory) && 'airport_pickup_required'
    }
  })
  const fieldsRef = useRef(fields)
  fieldsRef.current = fields

  useEffect(() => {
    fieldsToValidate(fields).forEach(f => {
      handleChange(f, fields[f].val, false)
    })
  }, [])

  const handleChange = (key, val, resetDependentObject = true) => {
    changeState(
      fieldsRef.current,
      key,
      val,
      (newObj) => {
        setFields({...fieldsRef.current, ...newObj})
      },
      resetDependentObject
    )
  }

  const submit = (e) => {
    e.preventDefault();
    setSubmitted(true)
    dispatch(productSubmit({
      profile_elements: {
        preferences: {
          dietary_requirements: fields.dietaryRequirements.val,
          dietary_requirements_other: fields.dietaryRequirementsOther.val
        },
        service: {
          service_level: fields.serviceLevel.val,
          insurance: fields.insurance.val,
          academic_support: fields.academicSupport.val,
          premium_student_care_required: fields.premiumStudentCareRequired.val,
          guardianship: fields.guardianship.val
        },
        placement: {
          airport_pickup_required: fields.airportPickupRequired.val
        }
      },
      user: user
    }))
  }

  const showPrices = (e) => {
    e.preventDefault();
    window.open(office.prices_page_url, '_blank');
  }

  const linkToPrices = () => (
    office.prices_page_url &&
    <ControlLabel>
      {
        ReactHtmlParser(formatMessage(
          defaultMessages.clientGuestPagesProductLabelsViewPrices,
          {office: office.name, prices_url: office.prices_page_url}
        ))
      }
    </ControlLabel>
  )

  const noOffice = () => (
    <Alert alert={content.pages.product.no_office} style={{background: '#ffdae0', border: 'solid 1px #ffc0cb'}}/>
  )

  const listProducts = () => {
    let serviceLevelLabel = productLocked ? 'clientGuestPagesProductLabelsProductLocked'
                                          : 'clientGuestPagesProductLabelsServiceLevel'
    if (productOptions.length > 0) {
      return (
        <div>
          <ReactMarkdown source={content.pages.product.body}/>
          {linkToPrices()}
          <CustomFormGroup field={fields.serviceLevel} className="product-checkbox">
            <ControlLabel>{formatMessage(defaultMessages[serviceLevelLabel])}</ControlLabel>
            <CheckboxOrRadioGroup
              setName={'serviceLevel'}
              type={'radio'}
              checkBoxStyle={'radio'}
              disabled={productLocked}
              controlFunc={(e) => handleChange('serviceLevel', e.target.value)}
              options={productOptions}
              selectedOptions={fields.serviceLevel.val}/>
          </CustomFormGroup>
        </div>
      )
    } else {
      return(<p>{content.pages.product.no_products}</p>)
    }
  }

  return (
    <Form page={page} thisPage={thisPage} skip={environment != 'production'} submitFunc={submit} disableSubmit={!pageValid(fields) || submitted} >
      <FormHeader title={content.pages.product.title} />
      {office ? listProducts() : noOffice()}

      <CustomFormGroup field={fields.dietaryRequirements}>
        <ControlLabel>{formatMessage(defaultMessages.clientGuestPagesProductLabelsDietaryRequirements)}</ControlLabel>
        <CheckboxOrRadioGroup
          setName={'dietaryRequirements'}
          type={'checkbox'}
          checkBoxStyle={'checkbox'}
          controlFunc={e => handleChange('dietaryRequirements', handleSelection(fields.dietaryRequirements.val, e.target.value))}
          options={dietSelections}
          selectedOptions={fields.dietaryRequirements.val}/>
      </CustomFormGroup>

      <CustomFormGroup field={fields.dietaryRequirementsOther}>
        <ControlLabel>{formatMessage(defaultMessages.clientGuestPagesProductLabelsDietOther)}</ControlLabel>
        <FormControl id="dietaryRequirementsOther"
          type="text"
          componentClass="input"
          defaultValue={fields.dietaryRequirementsOther.val}
          onChange={(e) => handleChange(e.target.id, e.target.value)}/>
      </CustomFormGroup>

      <CustomFormGroup field={fields.insurance}>
        <ControlLabel>{formatMessage(defaultMessages.clientGuestPagesProductLabelsInsurance)}</ControlLabel>
        <CheckboxOrRadioGroup
          setName={'insurance'}
          type={'radio'}
          checkBoxStyle={'radio'}
          controlFunc={(e) => handleChange('insurance', e.target.value)}
          options={insuranceOptions}
          selectedOptions={fields.insurance.val}/>
      </CustomFormGroup>

      <CustomFormGroup field={fields.academicSupport}>
        <ControlLabel>{formatMessage(defaultMessages.clientGuestPagesProductLabelsAcademicSupport)}</ControlLabel>
        <CheckboxOrRadioGroup
          setName={'academicSupport'}
          type={'radio'}
          checkBoxStyle={'radio'}
          controlFunc={(e) => handleChange('academicSupport', e.target.value)}
          options={academicSupportOptions}
          selectedOptions={fields.academicSupport.val}/>
      </CustomFormGroup>

      <CustomFormGroup field={fields.guardianship}>
        <ControlLabel>{formatMessage(defaultMessages.clientGuestPagesProductLabelsGuardianship)}</ControlLabel>
        <CheckboxOrRadioGroup
          setName={'guardianship'}
          type={'radio'}
          checkBoxStyle={'radio'}
          controlFunc={(e) => handleChange('guardianship', e.target.value)}
          options={guardianshipOptions}
          selectedOptions={fields.guardianship.val}/>
      </CustomFormGroup>

      <CustomFormGroup field={fields.premiumStudentCareRequired}>
        <ControlLabel>{formatMessage(defaultMessages.clientGuestPagesProductLabelsPremiumStudentCare)}</ControlLabel>
        <CheckboxOrRadioGroup
          setName={'premiumStudentCareRequired'}
          type={'radio'}
          checkBoxStyle={'radio'}
          controlFunc={(e) => handleChange('premiumStudentCareRequired', e.target.value)}
          options={premiumStudentCareOptions}
          selectedOptions={String(fields.premiumStudentCareRequired.val)}/>
      </CustomFormGroup>

      <CustomFormGroup field={fields.airportPickupRequired}>
        <ControlLabel>{content.pages.placement.apu_label}</ControlLabel>
        <FormControl id="airportPickupRequired"
          type="select"
          componentClass="select"
          defaultValue={fields.airportPickupRequired.val}
          onChange={e => handleChange(e.target.id, e.target.value)}>
          {yesNoOptions(formatMessage, false)}
        </FormControl>
      </CustomFormGroup>
    </Form>
  )
}

export default withDefaults(Product);
